// <s-photo-viewer> — full-screen product photo viewer inside dialog#product-photo-viewer.
// - Gallery buttons open the dialog declaratively (commandfor/show-modal) and carry data-viewer-index;
//   a capturing click listener remembers that index so the viewer opens on the tapped photo.
// - Swipe (scroll-snap track), prev/next buttons or arrow keys move between photos; counter follows.
// - Tap/click a photo to zoom 2x at that point; drag to pan while zoomed; tap again to zoom out.
//   Moving to another photo resets the zoom.
let pendingIndex = 0;
document.addEventListener(
  'click',
  (event) => {
    const trigger = event.target.closest?.('[data-viewer-index]');
    if (trigger) pendingIndex = Number(trigger.dataset.viewerIndex) || 0;
  },
  true
);

class PhotoViewer extends HTMLElement {
  #ac;
  #zoomed = null;
  #drag = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.dialog = this.closest('dialog');
    this.track = this.querySelector('[data-viewer-track]');
    this.slides = [...this.querySelectorAll('[data-viewer-slide]')];
    this.current = this.querySelector('[data-viewer-current]');
    if (!this.dialog || !this.track) return;

    this.dialog.addEventListener('toggle', (e) => {
      if (e.newState === 'open') {
        this.#goTo(pendingIndex, 'instant');
        // Load the neighbours of the opened photo straight away.
        this.slides.forEach((slide, i) => {
          if (Math.abs(i - pendingIndex) <= 1) slide.querySelector('img')?.setAttribute('loading', 'eager');
        });
      } else {
        this.#resetZoom();
      }
    }, opts);

    this.track.addEventListener('scroll', () => {
      const index = this.#index();
      if (this.current) this.current.textContent = String(index + 1);
      if (this.#zoomed && this.slides[index] !== this.#zoomed.closest('[data-viewer-slide]')) this.#resetZoom();
    }, { ...opts, passive: true });

    this.querySelector('[data-viewer-prev]')?.addEventListener('click', () => this.#goTo(this.#index() - 1), opts);
    this.querySelector('[data-viewer-next]')?.addEventListener('click', () => this.#goTo(this.#index() + 1), opts);
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.#goTo(this.#index() + (document.dir === 'rtl' ? 1 : -1));
      if (e.key === 'ArrowRight') this.#goTo(this.#index() + (document.dir === 'rtl' ? -1 : 1));
    }, opts);

    this.track.addEventListener('pointerdown', (e) => this.#onPointerDown(e), opts);
    this.track.addEventListener('pointermove', (e) => this.#onPointerMove(e), opts);
    this.track.addEventListener('pointerup', (e) => this.#onPointerUp(e), opts);
    this.track.addEventListener('pointercancel', () => (this.#drag = null), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #index() {
    const width = this.track.clientWidth || 1;
    return Math.min(this.slides.length - 1, Math.max(0, Math.round(Math.abs(this.track.scrollLeft) / width)));
  }

  #goTo(index, behavior = 'smooth') {
    const count = this.slides.length;
    if (!count) return;
    const target = (index + count) % count;
    this.#resetZoom();
    this.slides[target].scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    if (this.current) this.current.textContent = String(target + 1);
  }

  #onPointerDown(e) {
    const img = e.target.closest('[data-viewer-img]');
    if (!img) return;
    this.#drag = { img, x: e.clientX, y: e.clientY, moved: false, tx: Number(img.dataset.tx || 0), ty: Number(img.dataset.ty || 0) };
    if (this.#zoomed === img) img.setPointerCapture(e.pointerId);
  }

  #onPointerMove(e) {
    const d = this.#drag;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true;
    if (this.#zoomed !== d.img) return; // not zoomed: let the track swipe
    e.preventDefault();
    const rect = d.img.getBoundingClientRect();
    const maxX = rect.width / 4;
    const maxY = rect.height / 4;
    const tx = Math.max(-maxX, Math.min(maxX, d.tx + dx / 2));
    const ty = Math.max(-maxY, Math.min(maxY, d.ty + dy / 2));
    d.img.dataset.tx = String(tx);
    d.img.dataset.ty = String(ty);
    d.img.style.transform = `scale(2) translate(${tx}px, ${ty}px)`;
  }

  #onPointerUp(e) {
    const d = this.#drag;
    this.#drag = null;
    if (!d || d.moved) return;
    // A tap: toggle zoom at the tapped point.
    if (this.#zoomed === d.img) {
      this.#resetZoom();
      return;
    }
    this.#resetZoom();
    const rect = d.img.getBoundingClientRect();
    const ox = ((e.clientX - rect.left) / rect.width) * 100;
    const oy = ((e.clientY - rect.top) / rect.height) * 100;
    d.img.style.transformOrigin = `${ox}% ${oy}%`;
    d.img.style.transform = 'scale(2)';
    d.img.dataset.tx = '0';
    d.img.dataset.ty = '0';
    this.#zoomed = d.img;
    this.toggleAttribute('data-zoomed', true);
  }

  #resetZoom() {
    if (!this.#zoomed) return;
    this.#zoomed.style.transform = '';
    delete this.#zoomed.dataset.tx;
    delete this.#zoomed.dataset.ty;
    this.#zoomed = null;
    this.toggleAttribute('data-zoomed', false);
  }
}

if (!customElements.get('s-photo-viewer')) customElements.define('s-photo-viewer', PhotoViewer);
